'use client';

import type { MouseEvent } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { MARKETING_ROUTES } from '@/common/constants';
import { DemoLink } from '@/components/demo-link';
import { AccentedTitle, Section, SectionTitle } from '@/components/section';
import { smoothScrollToY } from '@/components/smooth-hash-scroll';
import { Button } from '@/components/ui/button';
import { FINAL_CTA_COPY } from '@/lib/content';

export function FinalCtaSection() {
  const pathname = usePathname();

  function handleScanClick(e: MouseEvent<HTMLAnchorElement>) {
    if (pathname !== '/') return;
    e.preventDefault();
    smoothScrollToY(0);
  }

  return (
    <Section
      id="book"
      className="invert-surface"
      contentClassName="flex flex-col items-center gap-6 text-center"
    >
      {/* Not italic as a whole: in the comp only the closing clause is, which is what makes it
              read as the turn rather than as one long quote. */}
      <SectionTitle size="quote" className="max-w-[820px]">
        <AccentedTitle text={FINAL_CTA_COPY.title} accent={FINAL_CTA_COPY.titleAccent} />
      </SectionTitle>
      <p className="max-w-[560px] text-lg leading-relaxed text-muted-foreground">
        {FINAL_CTA_COPY.body}
      </p>
      {/* Scan leads and demo follows, the same order of emphasis as the hero. This band is the
              one place both are full-size buttons: there is no scan field down here to carry the
              weight, so demoting the demo to a text link would leave the section with one control. */}
      <div className="mt-3 flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
        <Button size="lg" className="h-12 px-7 text-base" asChild>
          <Link href={MARKETING_ROUTES.home} onClick={handleScanClick}>
            {FINAL_CTA_COPY.scan}
          </Link>
        </Button>
        <Button variant="outline" size="lg" className="h-12 px-7 text-base" asChild>
          <DemoLink>{FINAL_CTA_COPY.demo}</DemoLink>
        </Button>
      </div>
    </Section>
  );
}
